import { useRouter } from 'next/router';
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Box, Drawer } from '@mantine/core';

import { HEADER_HEIGHT } from '@diagram/diagram-dimensions';

import { Component, PermissionComponent } from 'models';
import { diff } from 'helpers/diff';
import { StateHelpers } from 'helpers/StateHelpers';

import { ComponentChatButton } from 'components/component-chat/components/ComponentChatButton';
import { ComponentChatSidebar } from 'components/component-chat/components/ComponentChatSidebar';
import {
    componentChatState,
    componentChatStateDefaults,
    useComponentChat,
} from 'components/component-chat/hooks/useComponentChat';
import { ComponentDatasheet } from 'components/component-datasheet/components/ComponentDatasheet';
import { ComponentDatasheetActionsEdit } from 'components/component-datasheet/components/ComponentDatasheetActions.Edit';
import { Header, HeaderHero } from 'components/component-datasheet/components/sections/Header';
import { SubscriptionNotice } from 'components/component-datasheet/components/sections/SubscriptionNotice';
import { CurrentComponentContext } from 'components/component-datasheet/hooks/use-component-context';
import { ComponentForm } from 'components/component-form/ComponentForm';
import { AISidebar } from 'components/component-signup/components/AISidebar';
import { componentSignupState } from 'components/component-signup/state/index';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { ComponentIntercom } from 'components/intercom/types/ComponentIntercom';
import { WIDE_CONTENT_WIDTH } from 'components/page/Content';
import { Page } from 'components/page/Page';
import { StickyActions } from 'components/sticky-actions/StickyActions';
import { TableOfContents } from 'components/table-of-contents/TableOfContents';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponentPermissions } from 'hooks/use-component-permissions';
import { useCurrentUser } from 'hooks/use-current-user';
import { useIsMobile } from 'hooks/use-is-mobile';
import { useSnapshot } from 'hooks/use-safe-snapshot';
import { ComponentService } from 'services/ComponentService';
import { InternalTrackingService } from 'services/InternalTrackingService';

import { UnsavedChangesGuard } from './UnsavedChangesGuard';

import cx from './ComponentDetail.module.scss';

interface Props {
    component: Component;
    initialMode: DatasheetMode;
}

const ComponentDetail: FC<Props> = ({ component, initialMode }) => {
    const reinit = useRef(0);

    useLayoutEffect(() => {
        reinit.current += 1;
    }, []);

    const mode = useDatasheetMode();
    const permissions = useComponentPermissions(component);
    const canEdit = permissions[PermissionComponent.EDIT];

    const componentId = useWatch({
        name: 'id',
    });

    const user = useCurrentUser();
    const { company: manufacturer } = useCompanyProfile(component.manufacturer);

    const { chatOpen, toggleChatOpen } = useComponentChat();

    const hasFiles = component.files?.length > 0;

    const { aiOpened: showAiAssistant, aiOpenedExpanded } = useSnapshot(componentSignupState);
    const aiAssistantWidth = aiOpenedExpanded ? '70%' : '40%';

    const closeAiAssistant = () => {
        componentSignupState.aiOpened = false;
    };

    const router = useRouter();
    const { query, pathname } = router;

    // open chat if query param is present
    useEffect(() => {
        const { action, ...updatedQuery } = query;

        if (action === 'chat') {
            hasFiles && toggleChatOpen();

            const newPath = {
                pathname: pathname,
                query: updatedQuery,
            };

            router.replace(newPath, undefined, { shallow: true }).then();
        }
    }, [query, hasFiles, pathname, router, toggleChatOpen]);

    // This effect is dirty and could use a rework..
    useEffect(() => {
        if (mode === DatasheetMode.DUPLICATE && componentId) {
            ComponentService.navigate.view(componentId);
        }
    }, [componentId, mode]);

    const isMobile = useIsMobile('sm');
    const showAiChat = user && hasFiles && !isMobile;

    const stickyActionsHeight = canEdit ? 60 : 0;

    return (
        <Page>
            <UnsavedChangesGuard />

            <SubscriptionNotice component={component} />

            {canEdit && (
                <Box className={cx.stickyActions}>
                    <ComponentDatasheetActionsEdit wrapperProps={{ m: 'sm', justify: 'end' }} />
                </Box>
            )}

            {showAiChat && (
                <>
                    <Drawer
                        keepMounted
                        position="right"
                        withCloseButton={false}
                        opened={chatOpen}
                        onClose={toggleChatOpen}
                        styles={{
                            body: { padding: 0, height: '100%' },
                        }}
                    >
                        <ComponentChatSidebar component={component} />
                    </Drawer>
                    <Drawer
                        keepMounted
                        position="right"
                        withCloseButton={false}
                        opened={showAiAssistant}
                        onClose={closeAiAssistant}
                        styles={{
                            body: { padding: 0, height: '100%' },
                        }}
                        size={aiAssistantWidth}
                    >
                        <AISidebar />
                    </Drawer>
                </>
            )}

            <HeaderHero initialMode={initialMode} />

            <Page.Content maw={WIDE_CONTENT_WIDTH} className={cx.content} pt={0}>
                <Header />

                <TableOfContents
                    selector="[data-datasheet-section]"
                    reinit={reinit.current}
                    top={HEADER_HEIGHT + stickyActionsHeight}
                />

                <ComponentDatasheet component={component} initialMode={initialMode} />

                <StickyActions
                    mobile={
                        manufacturer && (
                            <ComponentIntercom
                                buttonProps={{ size: 'md', fullWidth: true }}
                                company={manufacturer}
                                component={component}
                            />
                        )
                    }
                    desktop={
                        <>
                            {showAiChat && <ComponentChatButton size="md" toggleChatOpen={toggleChatOpen} />}
                            {manufacturer && (
                                <ComponentIntercom
                                    buttonProps={{ size: 'md' }}
                                    company={manufacturer}
                                    component={component}
                                />
                            )}
                        </>
                    }
                />
            </Page.Content>
        </Page>
    );
};

const WrappedComponentDetail: FC<Props> = (props) => {
    const [currentComponent, setCurrentComponent] = useState<Component>(props.component);

    useEffect(() => {
        return () => {
            StateHelpers.reset(componentChatState, componentChatStateDefaults);
        };
    }, [props.component.id]);

    useEffect(() => {
        setCurrentComponent(props.component);
    }, [props.component.id]);

    return (
        <CurrentComponentContext.Provider
            value={{
                component: currentComponent,
                setComponent: setCurrentComponent,
            }}
        >
            <ComponentForm
                key={props.component.id}
                initialValues={props.component}
                onUpdate={(component) => {
                    setCurrentComponent(component);

                    const { updates, deletes } = diff(currentComponent, component);

                    InternalTrackingService.track('product.update', {
                        component,
                        updates,
                        deletes,
                    });
                }}
            >
                <ComponentDetail {...props} />
            </ComponentForm>
        </CurrentComponentContext.Provider>
    );
};

export { WrappedComponentDetail as ComponentDetail };
